import { useCallback } from "react";
import {
  useGetAllRegistrationFeeQuery,
  useAddRegistrationFeeMutation,
  useGetRegistrationFeeByIdQuery,
  useUpdateRegistrationFeeMutation,
  useDeleteRegistrationFeeMutation,
  useAttachRegistrationFeeCentersMutation,
  useDetachRegistrationFeeCentersMutation,
} from "../api/registrationFeeApi";
import { RegistrationFeeConfigPayloadType } from "../types/registrationFeeConfigRequest";
import { PaginationProps } from "../../../../core/data/interface";

export const useRegistrationFeeConfig = (params?: PaginationProps) => {
  const [addRegistrationFeeMutation, {isLoading: isAdding}] = useAddRegistrationFeeMutation();
  const [updateRegistrationFeeMutation, {isLoading: isUpdating}] = useUpdateRegistrationFeeMutation();
  const [deleteFeeConfigMutation] = useDeleteRegistrationFeeMutation();
  const [attachCentersMutation] = useAttachRegistrationFeeCentersMutation();
  const [detachCentersMutation] = useDetachRegistrationFeeCentersMutation();

  const { data, isLoading, error } = useGetAllRegistrationFeeQuery({
    page: params?.page || 1,
    size: params?.size || 10,
    search: params?.search || "",
  });

  const addFeeConfig = useCallback(
    async (feeConfig: RegistrationFeeConfigPayloadType) => {
      try {
        let result;
        result = await addRegistrationFeeMutation(feeConfig).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to add Registration Fee Configuration",
        };
      }
    },
    [addRegistrationFeeMutation]
  );

  const updateFeeConfig = useCallback(
    async (id: string, feeConfig: RegistrationFeeConfigPayloadType) => {
      try {
        let result;
        result = await updateRegistrationFeeMutation({ id, payload: feeConfig });
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to update registration fee configuration",
        };
      }
    },
    [updateRegistrationFeeMutation]
  );

  const deleteFeeConfig = useCallback(
    async (id: string) => {
      try {
        const result = await deleteFeeConfigMutation({ id }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to delete registration fee configuration",
        };
      }
    },
    [deleteFeeConfigMutation]
  );

  const attachCenters = useCallback(
    async (id: string, centerIds: number[]) => {
      try {
        const result = await attachCentersMutation({ id, payload: { center_ids: centerIds } }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to attach centers",
        };
      }
    },
    [attachCentersMutation]
  );

  const detachCenters = useCallback(
    async (id: string, centerIds: number[]) => {
      try {
        const result = await detachCentersMutation({ id, payload: { center_ids: centerIds } }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to detach centers",
        };
      }
    },
    [detachCentersMutation]
  );

  const useGetFeeConfigById = (id: string) => {
    const { data, error, isLoading } = useGetRegistrationFeeByIdQuery({id}, {
      skip: !id,
    });
    
    if (error) {
      console.error('Error fetching registration fee config by ID:', error);
    }
    return { feeConfig: data?.data || null, loading: isLoading, error };
  };
  

  return {
    feeConfigs: data?.data?.items || [],
    loading: isLoading,
    error,
    isAdding,
    isUpdating,
    addFeeConfig,
    updateFeeConfig,
    deleteFeeConfig,
    attachCenters,
    detachCenters,
    useGetFeeConfigById,
    paginationData: {
      pageSize: data?.data?.pageSize || 10,
      currentPage: data?.data?.currentPage || 1,
      totalItems: data?.data?.totalItems || 0,
      totalPages: data?.data?.totalPages || 0,
    },
  };
};
